import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Activity, Brain, Clock, Target, TrendingUp, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { agentService } from '@/lib/api/agent-service';
import type { AgentStatsSummary } from '@/types/agent-types-new';
import { SettingsConflictNotice } from './SettingsConflictNotice';

/** What this drawer renders: the recorded agent row, mapped by the caller. */
export interface AgentProfile {
  id: string;
  name: string;
  type: string;
  description?: string;
  status: string;
  category?: string;
}

interface AgentProfileDrawerProps {
  agent: AgentProfile | null;
  /** Measured from agent_logs; null when the agent has no rows or the summary failed. */
  stats?: AgentStatsSummary | null;
  statsError?: string | null;
  /** The summary request is in flight (first load or a retry): no empty-state claim is made. */
  statsLoading?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Writes agents.status through PUT /api/agents/:id; absent, the controls are not offered. */
  onStatusChange?: (agentId: string, status: 'active' | 'paused') => Promise<void> | void;
  statusPending?: boolean;
}

const NotRecorded = ({ what }: { what: string }) => <span className="text-muted-foreground italic">{what} not recorded</span>;

/**
 * Everything shown here is recorded or measured: settings come from
 * GET /api/agents/:id/settings (null fields are "not configured", never a default
 * model or temperature), statistics from the agent_logs summary (an agent with no
 * rows says so, never zero-filled), status is the agents.status column (its one
 * reader at origin/next is the conductor's active-agent roster). No Promote: no
 * maturity is persisted anywhere.
 */
export function AgentProfileDrawer({ agent, stats = null, statsError = null, statsLoading = false, open, onOpenChange, onStatusChange, statusPending = false }: AgentProfileDrawerProps) {
  const settingsQuery = useQuery({
    enabled: open && !!agent,
    queryKey: ['agent-settings', agent?.id],
    queryFn: () => agentService.getAgentSettings(agent!.id),
  });
  if (!agent) return null;
  const settings = settingsQuery.data;
  const successRate = agentService.successRateOf(stats);
  const terminal = stats ? stats.succeeded + stats.failed : 0;
  const nextStatus: 'active' | 'paused' = agent.status === 'paused' ? 'active' : 'paused';
  const num = (v: number | null | undefined, what: string) => (typeof v === 'number' ? <span className="text-sm text-muted-foreground">{v}</span> : <NotRecorded what={what} />);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Brain className="h-5 w-5" /><SheetTitle>{agent.name}</SheetTitle></div>
            <Badge variant={agent.status === 'active' ? 'default' : 'secondary'}>{agent.status}</Badge>
          </div>
          <SheetDescription>{agent.description}</SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="overview" className="mt-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="configuration">Configuration</TabsTrigger>
            <TabsTrigger value="performance">Performance</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <Card>
              <CardHeader><CardTitle>Agent Details</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div><h4 className="text-sm font-medium mb-1">ID</h4><p className="text-sm text-muted-foreground font-mono">{agent.id}</p></div>
                <div><h4 className="text-sm font-medium mb-1">Type</h4><Badge variant="outline">{agent.type}</Badge></div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Recorded status</h4>
                  <p className="text-sm text-muted-foreground">{agent.status}</p>
                  <p className="text-xs text-muted-foreground mt-1">agents.status is read by the conductor's active-agent roster; other runtimes do not consult it.</p>
                  {onStatusChange && (
                    <Button size="sm" variant="outline" className="mt-2" disabled={statusPending} onClick={() => onStatusChange(agent.id, nextStatus)}>
                      {statusPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      {nextStatus === 'paused' ? 'Pause' : 'Resume'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Quick Stats</CardTitle><CardDescription>Measured from agent_logs</CardDescription></CardHeader>
              <CardContent>
                {statsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading statistics</div>
                ) : statsError ? (
                  <p role="alert" className="text-sm text-red-700">Statistics could not be loaded: {statsError}</p>
                ) : !stats ? (
                  <p className="text-sm text-muted-foreground">No executions recorded for this agent.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2"><Activity className="h-4 w-4 text-muted-foreground" /><div><p className="text-sm font-medium">{stats.executions.toLocaleString()}</p><p className="text-xs text-muted-foreground">Executions</p></div></div>
                    <div className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-muted-foreground" /><div><p className="text-sm font-medium">{successRate === null ? <NotRecorded what="Terminal outcome" /> : `${successRate.toFixed(1)}%`}</p><p className="text-xs text-muted-foreground">Success rate over {terminal.toLocaleString()} terminal ({stats.succeeded.toLocaleString()} succeeded, {stats.failed.toLocaleString()} failed); {stats.nonterminal.toLocaleString()} pending or processing, {stats.unknown.toLocaleString()} unknown status</p></div></div>
                    <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-muted-foreground" /><div><p className="text-sm font-medium">{stats.avgDurationMs === null ? <NotRecorded what="Duration" /> : `${(stats.avgDurationMs / 1000).toFixed(2)}s`}</p><p className="text-xs text-muted-foreground">Avg duration ({stats.durationSamples.toLocaleString()} rows with a duration)</p></div></div>
                    <div className="flex items-center gap-2"><Target className="h-4 w-4 text-muted-foreground" /><div><p className="text-sm font-medium">{stats.lastAt ? format(new Date(stats.lastAt), 'MMM d, yyyy HH:mm') : <NotRecorded what="Last execution" />}</p><p className="text-xs text-muted-foreground">Last execution</p></div></div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="configuration" className="space-y-4">
            <Card>
              <CardHeader><CardTitle>Model Configuration</CardTitle><CardDescription>GET /api/agents/:id/settings</CardDescription></CardHeader>
              <CardContent className="space-y-4">
                {settingsQuery.isLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading settings</div>
                ) : settingsQuery.error ? (
                  <p role="alert" className="text-sm text-red-700">Settings could not be loaded: {settingsQuery.error instanceof Error ? settingsQuery.error.message : String(settingsQuery.error)}</p>
                ) : settings ? (
                  <>
                    {/* The route reports stores that disagree and malformed recorded values; a
                        value shown below is one of the disputed ones, or null for a malformed one,
                        and the notice says so (the detail page renders the same notice). */}
                    <SettingsConflictNotice conflicts={settings.conflicts} invalid={settings.invalid} />
                    <div><h4 className="text-sm font-medium mb-1">Model</h4>{settings.model ? <p className="text-sm text-muted-foreground">{settings.model}</p> : <NotRecorded what="Model" />}</div>
                    <div><h4 className="text-sm font-medium mb-1">Temperature</h4>{num(settings.temperature, 'Temperature')}</div>
                    <div><h4 className="text-sm font-medium mb-1">Token limit</h4>{num(settings.tokenLimit, 'Token limit')}</div>
                    <div><h4 className="text-sm font-medium mb-1">Max length</h4>{num(settings.maxLength, 'Max length')}</div>
                    {settings.prompt && (<div><h4 className="text-sm font-medium mb-1">Prompt</h4><pre className="text-sm whitespace-pre-wrap bg-muted p-3 rounded">{settings.prompt}</pre></div>)}
                  </>
                ) : null}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="performance" className="space-y-4">
            <Card>
              <CardHeader><CardTitle>Execution history</CardTitle><CardDescription>All agent_logs rows for this agent type</CardDescription></CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-1">
                {statsLoading ? (
                  <p>Loading statistics</p>
                ) : statsError ? (
                  <p role="alert" className="text-red-700">Statistics could not be loaded: {statsError}</p>
                ) : !stats ? (
                  <p>No executions recorded for this agent.</p>
                ) : (
                  <>
                    <p>Executions: {stats.executions.toLocaleString()}</p>
                    <p>Succeeded: {stats.succeeded.toLocaleString()}</p>
                    <p>Failed: {stats.failed.toLocaleString()}</p>
                    <p>Pending or processing: {stats.nonterminal.toLocaleString()}</p>
                    <p>Unknown status: {stats.unknown.toLocaleString()}</p>
                    <p>Success rate over terminal executions: {successRate === null ? 'no terminal execution recorded' : `${successRate.toFixed(1)}%`}</p>
                    <p>Average duration: {stats.avgDurationMs === null ? 'not recorded' : `${(stats.avgDurationMs / 1000).toFixed(2)}s`}</p>
                    <p>Last execution: {stats.lastAt ? format(new Date(stats.lastAt), 'MMM d, yyyy HH:mm') : 'not recorded'}</p>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="mt-6 flex gap-2">
          <Link to={`/agents/detail/${agent.id}`} className="flex-1"><Button className="w-full">Open agent page</Button></Link>
        </div>
      </SheetContent>
    </Sheet>
  );
}
